import React, { useState } from 'react';
import { useQuery } from 'react-query';
import api from '../utils/axios';
import { showSuccessToast, showErrorToast } from '../utils/toastUtils';
import {
  BarChart3,
  TrendingUp,
  TrendingDown,
  Calendar,
  Download,
  DollarSign,
  ShoppingCart,
  CheckCircle,
  Package,
  CreditCard,
  Banknote,
  Truck,
  Coffee,
  Utensils,
  FileText,
  Activity,
  Target,
  PieChart as PieChartIcon,
} from 'lucide-react';

const OrderReports = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [dateRange, setDateRange] = useState('week'); // week, month, custom
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [includeDetails, setIncludeDetails] = useState(false);

  // Get report summary
  const { data: summary } = useQuery(
    ['order-report-summary', dateRange],
    async () => {
      const response = await api.get(`/vendor/reports/orders/summary?period=${dateRange}`);
      return response.data;
    },
    {
      refetchInterval: 60000, // Refresh every minute
      retry: 3,
      retryDelay: 1000,
    }
  );

  // Get detailed report
  const { data: report, isLoading: reportLoading } = useQuery(
    ['order-report', startDate, endDate, includeDetails],
    async () => {
      const params = {};
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      if (includeDetails) params.include_details = 'true';
      
      const response = await api.get('/vendor/reports/orders', { params });
      return response.data;
    },
    {
      enabled: Boolean(startDate && endDate),
      retry: 3,
      retryDelay: 1000,
    }
  );

  const handleExport = async (format = 'csv') => {
    try {
      const params = { format };
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      
      // For summary exports, use the dateRange instead of specific dates
      if (!startDate && !endDate) {
        params.period = dateRange;
      }
      
      const response = await api.get('/vendor/reports/orders/export', { 
        params,
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const fileName = startDate && endDate 
        ? `order_report_${startDate}_to_${endDate}.${format}`
        : `order_report_${dateRange}.${format}`;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      showSuccessToast('Report exported successfully!');
    } catch (error) {
      showErrorToast('Failed to export report');
      console.error('Export error:', error);
    }
  };

  const getTrendIcon = (change) => {
    if (change > 0) return <TrendingUp className="h-4 w-4 text-green-500" />;
    if (change < 0) return <TrendingDown className="h-4 w-4 text-red-500" />;
    return <Activity className="h-4 w-4 text-gray-500" />;
  };

  const getTrendColor = (change) => {
    if (change > 0) return 'text-green-600';
    if (change < 0) return 'text-red-600';
    return 'text-gray-600';
  };

  const formatCurrency = (amount) => {
    return `£${parseFloat(amount || 0).toFixed(2)}`;
  };

  const formatPercentage = (value) => {
    return `${parseFloat(value || 0).toFixed(1)}%`;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'preparing': return 'bg-blue-100 text-blue-800';
      case 'ready': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPaymentMethodIcon = (method) => {
    switch (method) {
      case 'stripe': return <CreditCard className="h-4 w-4" />;
      case 'cash_at_counter': return <Banknote className="h-4 w-4" />;
      default: return <DollarSign className="h-4 w-4" />;
    }
  };

  const getOrderTypeIcon = (type) => {
    switch (type) {
      case 'delivery': return <Truck className="h-4 w-4" />;
      case 'takeaway': return <Package className="h-4 w-4" />;
      case 'dine_in': return <Utensils className="h-4 w-4" />;
      default: return <Coffee className="h-4 w-4" />;
    }
  };

  // Overview Tab Component
  const OverviewTab = () => (
    <div className=" ">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-secondary-600">Total Orders</p>
              <p className="text-2xl font-bold text-secondary-900">
                {summary?.summary?.total_orders || 0}
              </p>
              <div className="flex items-center mt-2">
                {getTrendIcon(summary?.trends?.orders_change)}
                <span className={`text-sm ml-1 ${getTrendColor(summary?.trends?.orders_change)}`}>
                  {summary?.trends?.orders_change > 0 ? '+' : ''}{summary?.trends?.orders_change || 0}%
                </span>
              </div>
            </div>
            <div className="bg-blue-500 p-3 rounded-lg">
              <ShoppingCart className="h-6 w-6 text-white" />
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-secondary-600">Total Revenue</p>
              <p className="text-2xl font-bold text-secondary-900">
                {formatCurrency(summary?.summary?.total_revenue)}
              </p>
              <div className="flex items-center mt-2">
                {getTrendIcon(summary?.trends?.revenue_change)}
                <span className={`text-sm ml-1 ${getTrendColor(summary?.trends?.revenue_change)}`}>
                  {summary?.trends?.revenue_change > 0 ? '+' : ''}{summary?.trends?.revenue_change || 0}%
                </span>
              </div>
            </div>
            <div className="bg-green-500 p-3 rounded-lg">
              <DollarSign className="h-6 w-6 text-white" />
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-secondary-600">Average Order Value</p>
              <p className="text-2xl font-bold text-secondary-900">
                {formatCurrency(summary?.summary?.average_order_value)}
              </p>
              <p className="text-xs text-secondary-500 mt-1">Per order</p>
            </div>
            <div className="bg-purple-500 p-3 rounded-lg">
              <Target className="h-6 w-6 text-white" />
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-secondary-600">Completion Rate</p>
              <p className="text-2xl font-bold text-secondary-900">
                {formatPercentage(summary?.summary?.completion_rate)}
              </p>
              <p className="text-xs text-secondary-500 mt-1">
                {summary?.summary?.completed_orders || 0} of {summary?.summary?.total_orders || 0} orders
              </p>
            </div>
            <div className="bg-orange-500 p-3 rounded-lg">
              <CheckCircle className="h-6 w-6 text-white" />
            </div>
          </div>
        </div>
      </div>

      {/* Today vs Yesterday Comparison */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="card">
          <h3 className="text-lg font-semibold text-secondary-900 mb-4">Today's Performance</h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-secondary-600">Orders:</span>
              <span className="font-medium">{summary?.today?.orders || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-secondary-600">Revenue:</span>
              <span className="font-medium">{formatCurrency(summary?.today?.revenue)}</span>
            </div>
          </div>
        </div>

        <div className="card">
          <h3 className="text-lg font-semibold text-secondary-900 mb-4">Yesterday's Performance</h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-secondary-600">Orders:</span>
              <span className="font-medium">{summary?.yesterday?.orders || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-secondary-600">Revenue:</span>
              <span className="font-medium">{formatCurrency(summary?.yesterday?.revenue)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="card">
        <h3 className="text-lg font-semibold text-secondary-900 mb-4">Quick Actions</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <button
            onClick={() => setActiveTab('detailed')}
            className="flex items-center justify-center space-x-2 p-4 border border-secondary-200 rounded-lg hover:bg-secondary-50 transition-colors"
          >
            <BarChart3 className="h-5 w-5 text-primary-600" />
            <span className="font-medium">Detailed Report</span>
          </button>
          <button
            onClick={() => handleExport('csv')}
            className="flex items-center justify-center space-x-2 p-4 border border-secondary-200 rounded-lg hover:bg-secondary-50 transition-colors"
          >
            <Download className="h-5 w-5 text-green-600" />
            <span className="font-medium">Export CSV</span>
          </button>
          <button
            onClick={() => handleExport('json')}
            className="flex items-center justify-center space-x-2 p-4 border border-secondary-200 rounded-lg hover:bg-secondary-50 transition-colors"
          >
            <FileText className="h-5 w-5 text-blue-600" />
            <span className="font-medium">Export JSON</span>
          </button>
        </div>
      </div>
    </div>
  );

  // Detailed Report Tab Component
  const DetailedReportTab = () => (
    <div className=" ">
      {/* Date Range Filter */}
      <div className="card">
        <h3 className="text-lg font-semibold text-secondary-900 mb-4">Report Filters</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-secondary-700 mb-2">Start Date</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="input-field"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-secondary-700 mb-2">End Date</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="input-field"
            />
          </div>
          <div className="flex items-end">
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={includeDetails}
                onChange={(e) => setIncludeDetails(e.target.checked)}
                className="rounded border-secondary-300 text-primary-600 focus:ring-primary-500"
              />
              <span className="text-sm text-secondary-700">Include Order Details</span>
            </label>
          </div>
          <div className="flex items-end">
            <button
              onClick={() => handleExport('csv')}
              disabled={!startDate || !endDate}
              className="btn-primary w-full"
            >
              <Download className="h-4 w-4 mr-2" />
              Export
            </button>
          </div>
        </div>
      </div>

      {reportLoading ? (
        <div className="card">
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
            <span className="ml-2 text-secondary-600">Loading report...</span>
          </div>
        </div>
      ) : report ? (
        <>
          {/* Report Summary */}
          <div className="card">
            <h3 className="text-lg font-semibold text-secondary-900 mb-4">Report Summary</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center">
                <p className="text-2xl font-bold text-secondary-900">{report.summary.total_orders}</p>
                <p className="text-sm text-secondary-600">Total Orders</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-secondary-900">{formatCurrency(report.summary.total_revenue)}</p>
                <p className="text-sm text-secondary-600">Total Revenue</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-secondary-900">{formatCurrency(report.summary.average_order_value)}</p>
                <p className="text-sm text-secondary-600">Avg Order Value</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-secondary-900">{formatPercentage(report.summary.completion_rate)}</p>
                <p className="text-sm text-secondary-600">Completion Rate</p>
              </div>
            </div>
          </div>

          {/* Top Selling Items */}
          {report.top_selling_items && report.top_selling_items.length > 0 && (
            <div className="card">
              <h3 className="text-lg font-semibold text-secondary-900 mb-4">Top Selling Items</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-secondary-200">
                  <thead className="bg-secondary-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">Item</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">Quantity Sold</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">Revenue</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">Avg Price</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-secondary-200">
                    {report.top_selling_items.map((item, index) => (
                      <tr key={index}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-secondary-900">
                          {item.name}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-500">
                          {item.total_quantity}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-500">
                          {formatCurrency(item.total_revenue)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-500">
                          {formatCurrency(item.average_price)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Status Distribution */}
          {report.status_distribution && report.status_distribution.length > 0 && (
            <div className="card">
              <h3 className="text-lg font-semibold text-secondary-900 mb-4">Order Status Distribution</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-3">
                  {report.status_distribution.map((status, index) => (
                    <div key={index} className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(status.status)}`}>
                          {status.status}
                        </span>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-medium text-secondary-900">{status.count}</p>
                        <p className="text-xs text-secondary-500">{formatPercentage(status.percentage)}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="flex items-center justify-center">
                  <div className="text-center">
                    <PieChartIcon className="h-16 w-16 text-secondary-300 mx-auto mb-2" />
                    <p className="text-sm text-secondary-500">Chart visualization coming soon</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Payment Method Distribution */}
          {report.payment_method_distribution && report.payment_method_distribution.length > 0 && (
            <div className="card">
              <h3 className="text-lg font-semibold text-secondary-900 mb-4">Payment Method Distribution</h3>
              <div className="space-y-3">
                {report.payment_method_distribution.map((payment, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-secondary-50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      {getPaymentMethodIcon(payment.method)}
                      <span className="font-medium text-secondary-900 capitalize">
                        {payment.method.replace('_', ' ')}
                      </span>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium text-secondary-900">{payment.count} orders</p>
                      <p className="text-xs text-secondary-500">{formatCurrency(payment.total_amount)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Detailed Orders */}
          {includeDetails && report.detailed_orders && (
            <div className="card">
              <h3 className="text-lg font-semibold text-secondary-900 mb-4">Detailed Orders</h3>
              <div className="space-y-4">
                {report.detailed_orders.map((order) => (
                  <div key={order.id} className="border border-secondary-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center space-x-4">
                        <span className="font-medium text-secondary-900">Order #{order.id}</span>
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(order.status)}`}>
                          {order.status}
                        </span>
                        {getOrderTypeIcon(order.order_type)}
                        <span className="text-sm text-secondary-600 capitalize">
                          {order.order_type.replace('_', ' ')}
                        </span>
                      </div>
                      <div className="text-right">
                        <p className="font-medium text-secondary-900">{formatCurrency(order.total_amount)}</p>
                        <p className="text-xs text-secondary-500">
                          {new Date(order.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    {order.items && order.items.length > 0 && (
                      <div className="bg-secondary-50 rounded-lg p-3">
                        <p className="text-sm font-medium text-secondary-700 mb-2">Items:</p>
                        <div className="space-y-1">
                          {order.items.map((item, index) => (
                            <div key={index} className="flex justify-between text-sm">
                              <span>{item.quantity}x {item.name}</span>
                              <span>{formatCurrency(item.total_price)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="card">
          <div className="text-center py-8">
            <Calendar className="h-12 w-12 text-secondary-400 mx-auto mb-4" />
            <p className="text-secondary-600">Select date range to generate detailed report</p>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className=" ">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-secondary-900">Order Reports</h1>
          <p className="text-sm md:text-base text-secondary-600">
            Analyze your sales performance and order trends
          </p>
        </div>
        
        <div className="flex items-center space-x-2">
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            className="input-field text-sm"
          >
            <option value="week">Last Week</option>
            <option value="month">Last Month</option>
            <option value="year">Last Year</option>
          </select>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-secondary-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('overview')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'overview'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-secondary-500 hover:text-secondary-700 hover:border-secondary-300'
            }`}
          >
            <BarChart3 className="h-4 w-4 inline mr-2" />
            Overview
          </button>
          <button
            onClick={() => setActiveTab('detailed')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'detailed'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-secondary-500 hover:text-secondary-700 hover:border-secondary-300'
            }`}
          >
            <FileText className="h-4 w-4 inline mr-2" />
            Detailed Report
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' ? <OverviewTab /> : <DetailedReportTab />}
    </div>
  );
};

export default OrderReports; 